import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { Stack, Typography, Box, Button } from '@mui/material';
import { supportedPairingBankNames } from '@features/profile/entity';
import { GuideStep } from './onboarding-guide-types';
import { useEffect, useState } from 'react';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
};

const InstallPrompt = ({ onInstalled }: { onInstalled: () => void }) => {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);

  useEffect(() => {
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
  }, []);

  if (!installPrompt) return null;

  return (
    <Button
      type="button"
      onClick={async () => {
        await installPrompt.prompt();
        onInstalled();
      }}
    >
      Nainstalovat aplikaci
    </Button>
  );
};

export const StepDetailContent = ({
  step,
  onAppInstallDone,
}: {
  step: GuideStep;
  onAppInstallDone?: () => void;
}) => {
  const { t } = useIntlTranslations('onboardingGuide');

  if (step.key === 'appInstallGuide') {
    return (
      <Stack spacing={1.5} id="app-install-guide">
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          <strong>iPhone:</strong> otevřete Kadeřník Plus v Safari, klepněte na Sdílet a zvolte
          Přidat na plochu.
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          <strong>Android:</strong> otevřete web v Chrome, klepněte na nabídku a zvolte Přidat na
          plochu nebo Instalovat aplikaci.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            href="https://support.apple.com/cs-cz/guide/iphone/iphea86e5236/ios"
            target="_blank"
            rel="noreferrer"
          >
            Postup pro iPhone
          </Button>
          <Button
            href="https://www.youtube.com/watch?v=PKC4W_nphGM"
            target="_blank"
            rel="noreferrer"
          >
            Postup pro Android
          </Button>
        </Stack>
        <InstallPrompt onInstalled={onAppInstallDone ?? (() => undefined)} />
        <Button variant="outlined" onClick={onAppInstallDone}>
          Označit krok jako hotový
        </Button>
      </Stack>
    );
  }

  if (step.key === 'qrPaymentsSetup') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('qrPaymentsSetup.tip1', { banks: supportedPairingBankNames() })}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('qrPaymentsSetup.tip2')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('qrPaymentsSetup.tip3')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('qrPaymentsSetup.tip4')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('qrPaymentsSetup.tip5')}
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'stockItemCreated') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('stock.tip1')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('stock.tip2')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('stock.tip3')}
        </Typography>
        <Box
          component="img"
          src="/assets/images/stock_tutorial.png"
          alt={t('stock.imgAlt')}
          sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
        />
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('stock.tip4')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('stock.tip5')}
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'reservationEnabled') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('reservation.tip1')}
        </Typography>
        <Box
          component="img"
          src="/assets/onboarding-images/reservation-services-allowed.png"
          alt={t('reservation.imgAlt')}
          sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
        />
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('reservation.tip2')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('reservation.tip3')}
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'visitCreated') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('visit.tip1')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          <ol>
            <li>{t('visit.li1')}</li>
            <li>{t('visit.li2')}</li>
            <li>{t('visit.li3')}</li>
          </ol>
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'clientCreated') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('clients.tip1')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          <ol>
            <li>{t('clients.li1')}</li>
            <li>{t('clients.li2')}</li>
          </ol>
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'serviceCreated') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('services.tip1')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('services.tip2')}
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'publicReservationSetup') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('publicReservation.tip1a')}
          <strong>{t('publicReservation.tip1b')}</strong>,{' '}
          <strong>{t('publicReservation.tip1c')}</strong>
          {t('publicReservation.tip1d')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('publicReservation.tip2')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('publicReservation.tip3')}
        </Typography>
      </Stack>
    );
  }

  if (step.key === 'workScheduleConfigured') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('workSchedule.tip1')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {t('workSchedule.tip2')}
          <li>
            <ol>{t('workSchedule.li1')}</ol>
            <ol>{t('workSchedule.li2')}</ol>
          </li>
        </Typography>
        <Box
          component="img"
          src="/assets/onboarding-images/overview-visits.png"
          alt={t('workSchedule.imgAlt')}
          sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
        />
      </Stack>
    );
  }

  return null;
};
